import React, { useState, useEffect, useCallback } from 'react';
import '../IpPatientDetails.css';
import { useLocation } from 'react-router-dom';
import {
  Box, Button, Checkbox, FormControlLabel, Paper, Typography,
} from '@mui/material';
import { CommonPatientDetails } from '../../../../../Components/Common Components/CommonComponents';
import { getCachevalue } from '../../../../../Utility/Services/CacheProviderService';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../../Components/Loader';
import { Colors } from '../../../../../Styles/Colors';

export default function IPChecklist({ from = 'Admission' }) {
  const defaultType = from === 'discharge' ? 'discharge' : 'admission';

  const [patientData, setPatientData] = useState({});
  const [withOutAppointment, setWithOutAppointment] = useState({});
  const [checklistType, setChecklistType] = useState(defaultType);
  const [categories, setCategories] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const ip_admission_id = patientData?.id || withOutAppointment?.ip_admission_id;

  const fetchPatient = useCallback(() => {
    const raw = getCachevalue('IppatientData');
    if (!raw) return;
    try {
      setPatientData(JSON.parse(raw));
    } catch (e) { }
    const appRaw = getCachevalue('withoutAppointment');
    if (appRaw) {
      try {
        const app = JSON.parse(appRaw);
        if (app?.patient_id === JSON.parse(raw)?.patient_id) setWithOutAppointment(app);
      } catch (e) { }
    }
  }, []);

  const fetchChecklist = useCallback(() => {
    if (!ip_admission_id) return;
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_PATIENT_CHECKLIST}?ip_admission_id=${ip_admission_id}&type=${checklistType}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          const data = res.data?.data;
          const list = Array.isArray(data) ? data : [];
          setCategories(list.map((cat) => ({
            ...cat,
            items: (cat.items || []).map((it) => ({
              ...it,
              is_checked: !!it.is_checked,
              checked_from_api: !!it.is_checked,
            })),
          })));
        } else setCategories([]);
        setIsLoader(false);
      })
      .catch(() => {
        setCategories([]);
        setIsLoader(false);
      });
  }, [ip_admission_id, checklistType]);

  useEffect(() => { fetchPatient(); }, [fetchPatient]);
  useEffect(() => { setChecklistType(from); }, [from]);
  useEffect(() => { if (ip_admission_id) fetchChecklist(); }, [ip_admission_id, fetchChecklist]);

  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const toggleItem = (categoryIndex, itemIndex, checked) => {
    setCategories((prev) => {
      const next = prev.map((cat, ci) => {
        if (ci !== categoryIndex) return cat;
        const items = (cat.items || []).map((it, ii) => {
          if (ii !== itemIndex) return it;
          if (it.checked_from_api) return it;
          return { ...it, is_checked: !!checked };
        });
        return { ...cat, items };
      });
      return next;
    });
  };

  const handleSave = () => {
    if (!ip_admission_id) { showError('Patient context missing.'); return; }
    setSaveLoading(true);
    const categoriesPayload = categories.map((cat) => ({
      id: cat.id ?? null,
      category_title: cat.category_title,
      checklist_type: cat.checklist_type || checklistType,
      order: cat.order ?? 0,
      items: (cat.items || []).map((it, idx) => ({
        id: it.id ?? null,
        check_list_id: it.check_list_id ?? null,
        item_name: it.item_name,
        order: it.order ?? idx + 1,
        is_checked: !!it.is_checked,
      })),
    }));
    const payload = { ip_admission_id, checklist_type: checklistType, categories: categoriesPayload };
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_CHECKLIST)
      .then((res) => {
        setSaveLoading(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Checklist saved.');
          fetchChecklist();
        } else showError(res.data?.message || 'Save failed.');
      })
      .catch((err) => {
        setSaveLoading(false);
        showError(err?.response?.data?.message || err?.message || 'Save failed.');
      });
  };

  const title = checklistType === 'admission' ? 'Patient Admission Checklist' : 'Patient Discharge Checklist';

  return (
    <Box sx={{ backgroundColor: Colors.configHomeBg, minHeight: '74dvh', p: '0.625vw' }}>
      <Box sx={{ mb: 1 }}>
        <CommonPatientDetails data={patientData} showDetailed={true} />
      </Box>

      <Paper variant="outlined" sx={{ p: 1}}>
        {/* <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography> */}
        <Box sx={{height:'55vh', overflow:'auto'}}>
        {categories.map((cat, categoryIndex) => (
          <Box key={cat.id ?? categoryIndex} sx={{ mb: 1}}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, }}>
              {(categoryIndex + 1)}. {cat.category_title || 'Category'}
            </Typography>
            <Box sx={{ pl: 1 }}>
              {(cat.items || []).map((item, itemIndex) => (
                <Box key={item?.id ?? itemIndex} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <Checkbox
                    checked={!!item.is_checked}
                    onChange={(e) => toggleItem(categoryIndex, itemIndex, e.target.checked)}
                    disabled={!!item.checked_from_api}
                    color="primary"
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2">
                    {item?.item_name || '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
        {categories.length === 0 && !isLoader && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No checklist items. Configure from Hospital Configuration → Checklist Master.
          </Typography>
        )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="contained" onClick={handleSave} disabled={saveLoading} sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary || '#04B7B1' }}>
            {saveLoading ? 'Saving...' : 'Continue'}
          </Button>
        </Box>
      </Paper>

      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
